import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { LoadingSpinner } from './components/LoadingSpinner';
import { AuthGuard } from './features/auth/guards/AuthGuard';
import { GuestGuard } from './features/auth/guards/GuestGuard';
import { Toaster } from '@/components/ui/sonner';
import { NotificationProvider } from './features/notifications/context/NotificationProvider';

// Lazy load pages for code splitting - feature module paths
const Dashboard = lazy(() => import('./features/dashboard/pages/DashboardPage'));
const Accounts = lazy(() => import('./features/accounts/pages/AccountsPage'));
const Transactions = lazy(() => import('./features/transactions/pages/TransactionsPage'));
const TransactionDetail = lazy(() => import('./features/transactions/pages/TransactionDetailPage'));
const Categories = lazy(() => import('./features/categorization/pages/CategoriesPage'));
const Budgets = lazy(() => import('./features/budgets/pages/BudgetsPage'));
const Banks = lazy(() => import('./features/banking/pages/BanksPage'));
const Recurring = lazy(() => import('./features/recurring/pages/RecurringPage'));
const RecurringDetail = lazy(() => import('./features/recurring/pages/RecurringDetailPage'));
const Reports = lazy(() => import('./features/reports/pages/ReportsPage'));
const Savings = lazy(() => import('./features/savings/pages/SavingsPage'));
const SpendingPlan = lazy(() => import('./features/spending-plan/pages/SpendingPlanPage'));
const Debts = lazy(() => import('./features/debt/pages/DebtsPage'));
const DebtDetail = lazy(() => import('./features/debt/pages/DebtDetailPage'));
const MortgageDetail = lazy(() => import('./features/debt/pages/MortgageDetailPage'));
const Settings = lazy(() => import('./features/settings/pages/SettingsPage'));
const BankCallback = lazy(() => import('./features/banking/pages/BankCallbackPage'));
const Styleguide = lazy(() => import('./pages/Styleguide'));
const Login = lazy(() => import('./features/auth/pages/LoginPage'));
const Register = lazy(() => import('./features/auth/pages/RegisterPage'));
const Finance = lazy(() => import('./features/finance/pages/FinanceHubPage'));
const Import = lazy(() => import('./features/import/pages/ImportPage'));
const AdminLayout = lazy(() => import('./features/admin/components/AdminLayout'));
const UsersAdmin = lazy(() => import('./features/admin/pages/UsersAdminPage'));
const FeatureFlagsAdmin = lazy(() => import('./features/admin/pages/FeatureFlagsAdminPage'));
const ManagedCategoriesAdmin = lazy(() => import('./features/admin/pages/ManagedCategoriesAdminPage'));
const ManagedMerchantsAdmin = lazy(() => import('./features/admin/pages/ManagedMerchantsAdminPage'));
const BankListAdmin = lazy(() => import('./features/admin/pages/BankListPage'));
const BankDetailAdmin = lazy(() => import('./features/admin/pages/BankDetailPage'));
const CronJobsAdmin = lazy(() => import('./features/admin/pages/CronJobsAdminPage'));
const NotFound = lazy(() => import('./pages/NotFound'));

function App() {
  return (
    <ErrorBoundary>
      <NotificationProvider>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public routes - redirect to dashboard if authenticated */}
          <Route
            path="/login"
            element={
              <GuestGuard>
                <Login />
              </GuestGuard>
            }
          />
          <Route
            path="/register"
            element={
              <GuestGuard>
                <Register />
              </GuestGuard>
            }
          />

          {/* Bank OAuth callback - public but requires code parameter */}
          <Route path="/banks/callback" element={<BankCallback />} />

          {/* Styleguide - public for development reference */}
          <Route path="/styleguide" element={<Styleguide />} />

          {/* Protected routes - require authentication */}
          <Route
            path="/"
            element={
              <AuthGuard>
                <Layout>
                  <Navigate to="/dashboard" replace />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/dashboard"
            element={
              <AuthGuard>
                <Layout>
                  <Dashboard />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/finance/:tab?"
            element={
              <AuthGuard>
                <Layout>
                  <Finance />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/accounts"
            element={
              <AuthGuard>
                <Layout>
                  <Accounts />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/transactions"
            element={
              <AuthGuard>
                <Layout>
                  <Transactions />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/transactions/:id"
            element={
              <AuthGuard>
                <Layout>
                  <TransactionDetail />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/categories"
            element={
              <AuthGuard>
                <Layout>
                  <Categories />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/budgets"
            element={
              <AuthGuard>
                <Layout>
                  <Budgets />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/banks"
            element={
              <AuthGuard>
                <Layout>
                  <Banks />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/recurring"
            element={
              <AuthGuard>
                <Layout>
                  <Recurring />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/recurring/:id"
            element={
              <AuthGuard>
                <Layout>
                  <RecurringDetail />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/reports/:tab?"
            element={
              <AuthGuard>
                <Layout>
                  <Reports />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/savings"
            element={
              <AuthGuard>
                <Layout>
                  <Savings />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/spending-plan"
            element={
              <AuthGuard>
                <Layout>
                  <SpendingPlan />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/debts"
            element={
              <AuthGuard>
                <Layout>
                  <Debts />
                </Layout>
              </AuthGuard>
            }
          />
          {/* Literal tab segments must precede the dynamic /:id route so they
              don't get swallowed as a debt id. */}
          <Route
            path="/debts/dashboard"
            element={
              <AuthGuard>
                <Layout>
                  <Debts />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/debts/strategies"
            element={
              <AuthGuard>
                <Layout>
                  <Debts />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/debts/mortgage/:mortgageId"
            element={
              <AuthGuard>
                <Layout>
                  <MortgageDetail />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/debts/:id"
            element={
              <AuthGuard>
                <Layout>
                  <DebtDetail />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/settings"
            element={
              <AuthGuard>
                <Layout>
                  <Settings />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin"
            element={
              <AuthGuard>
                <Layout>
                  <Navigate to="/admin/managed-merchants" replace />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/managed-merchants"
            element={
              <AuthGuard>
                <Layout>
                  <AdminLayout>
                    <ManagedMerchantsAdmin />
                  </AdminLayout>
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/categories"
            element={
              <AuthGuard>
                <Layout>
                  <AdminLayout>
                    <ManagedCategoriesAdmin />
                  </AdminLayout>
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AuthGuard>
                <Layout>
                  <AdminLayout>
                    <UsersAdmin />
                  </AdminLayout>
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/feature-flags"
            element={
              <AuthGuard>
                <Layout>
                  <AdminLayout>
                    <FeatureFlagsAdmin />
                  </AdminLayout>
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/banks"
            element={
              <AuthGuard>
                <Layout>
                  <AdminLayout>
                    <BankListAdmin />
                  </AdminLayout>
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/banks/:id"
            element={
              <AuthGuard>
                <Layout>
                  <AdminLayout>
                    <BankDetailAdmin />
                  </AdminLayout>
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/admin/cron-jobs"
            element={
              <AuthGuard>
                <Layout>
                  <AdminLayout>
                    <CronJobsAdmin />
                  </AdminLayout>
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/import"
            element={
              <AuthGuard>
                <Layout>
                  <Import />
                </Layout>
              </AuthGuard>
            }
          />
          <Route
            path="/settings/banks"
            element={
              <AuthGuard>
                <Layout>
                  <Settings />
                </Layout>
              </AuthGuard>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Toaster />
      </NotificationProvider>
    </ErrorBoundary>
  );
}

export default App;
